import { forkJoin, Observable, of } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { saveAs } from 'file-saver'

import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'

import { environment } from 'src/environments/environment'

import { EndpointOptions, RequestService } from './request.service'

// ============================================
// Interfaces
// ============================================

export interface OpsStationConnection {
    id: string
    site_id: string
    from_station_id: string
    to_station_id: string
    label: string | null
    exit_side: string | null
    entry_side: string | null
    sort_order: number
    from_station_name: string
    to_station_name: string
    created_at: string
    updated_at: string
}

export interface StationEquipment {
    equipment_id: string
    name: string
    code: string
}

export interface OpsStation {
    id: string
    site_id: string
    station_code: string
    station_name: string
    is_archived?: boolean
    machine_only?: boolean
    allows_batch_operators?: boolean
    /** Whether a job may run here concurrently with another allow-concurrent-jobs station (Diss/Clean lines). */
    allow_concurrent_jobs?: boolean
}
export interface OpsProductFamily {
    id: string
    site_id: string
    family_code: string
    family_name: string
    description: string | null
    is_archived: boolean
    sort_order?: number
    created_at: string
    updated_at: string
}

export interface OpsProduct {
    id: string
    family_id: string
    product_code: string
    product_name: string
    description: string | null
    default_quantity: number | null
    target_scrap_rate: number | null
    unit_value: number | null
    is_active: boolean
    sort_order: number
    family_code?: string
    family_name?: string
    created_at: string
    updated_at: string
}

export interface OpsLaborEntry {
    id: string
    site_id: string
    user_id: string
    batch_id: string | null
    batch_event_id?: string | null
    /** TRUE when the underlying machine_event has been resolved (job is done).
     * Drives the My Shifts "Complete job" quick-action visibility. */
    batch_event_resolved?: boolean
    /** Non-null when the operator explicitly completed the job at this station
     * (set by complete_batch, post-review approval, upstream auto-resolve).
     * A plain clock-out without Complete leaves this NULL — the job is
     * "idle, yet to be completed at this station". Drives the pressed/green
     * state on the My Shifts "Complete job" quick action. */
    batch_event_end_ts?: string | null
    clock_in_ts: string
    clock_out_ts: string | null
    duration_hours: number | null
    notes: string | null
    tags: string[] | null
    batch_number: string | null
    source: 'shift' | 'batch'
    created_at: string
    updated_at: string
    station_id?: string | null
    station_name?: string | null
    is_multi_job?: boolean
    /** Whether this direct job labor entry counts as rework. Null/undefined means unknown. */
    is_rework?: boolean | null
    allocated_batch_numbers?: string[] | null
}

export type LaborEntryClassification = 'standard' | 'rework'

export interface LaborEntryStationOption {
    station_id: string
    station_name: string
    /** Non-null when the batch has already been at this station (direct re-link). */
    batch_event_id: string | null
    batch_details_id: string | null
    /** Canonical source event for this batch; used to ensure-batch at a new station. */
    source_batch_event_id: string
    /** Whether this station allows concurrent jobs (suppresses the "active elsewhere" sanity warning). */
    allow_concurrent_jobs?: boolean
}

export interface TimeEntryAllocation {
    allocation_id: string
    batch_id: string
    batch_event_id: string
    batch_number: string
    allocation_weight: number
}

export type RecentlyClosedReason = 'resolved' | 'archived' | 'aborted' | 'completed'

export interface AvailableBatch {
    batch_event_id: string
    batch_number: string
    work_order: string | null
    equipment_id: string
    equipment_name: string
    product_name?: string | null
    latest_time_entry_ts?: string | null
    started_at: string
    ended_at?: string | null
    operator_count: number
    status: 'running' | 'idle' | 'completed' | 'aborted'
    /** Whether the batch's station allows concurrent jobs (suppresses the "active elsewhere" sanity warning). */
    allow_concurrent_jobs?: boolean
    /**
     * Non-null when the row was surfaced by the recently-closed (7-day) recovery
     * arm of get_available_batches_for_site. Drives the "Recently {reason}" pill
     * in the Add Shift / Edit Shift / Join Job pickers so the operator knows
     * selecting the row will reopen the closed batch.
     */
    recently_closed_reason?: RecentlyClosedReason | null
}

export interface StationJob {
    batch_event_id: string
    station_id: string
    station_name: string
    station_code: string
    batch_number: string
    work_order: string | null
    batch_status: 'running' | 'idle' | 'completed'
    start_ts: string | null
    end_ts: string | null
    product_name: string | null
    operator_count: number
    is_resolved: boolean
}

export interface StationDowntimeEvent {
    event_id: string
    equipment_id: string
    equipment_name: string
    start_ts: string
    end_ts: string | null
    duration_minutes: number | null
    downtime_reason_id: string | null
    reason_name: string | null
    reason_description: string | null
    reason_code: string | null
    reason_type: 'planned' | 'unplanned' | 'idle' | null
    notes: string | null
    troubleshooting_notes: string | null
    resolution_notes: string | null
    resolved_at: string | null
    resolved_by: string | null
    resolved_by_name: string | null
    created_by: string | null
    created_by_name: string | null
    troubleshooting_started_by: string | null
    troubleshooting_started_by_name: string | null
}

export interface StationDowntimeSummary {
    station_id: string
    total_count: number
    planned_count: number
    unplanned_count: number
    active_count: number
    unknown_count: number
    total_minutes: number
    planned_minutes: number
    unplanned_minutes: number
    active_minutes: number
    unknown_minutes: number
}

export interface EnsuredBatchAtStation {
    batch_event_id: string
    batch_details_id: string
    batch_number: string
    work_order: string | null
    equipment_id: string
    created: boolean
}

export interface LoggedCompletedJobSlice {
    batch_event_id: string
    batch_details_id: string
    batch_number: string
    work_order: string | null
    equipment_id: string
    created: boolean
    completed_batch: boolean
}

export interface CompletedLaborEntryResult {
    entry: OpsLaborEntry
    action: 'completed-time-entry-only' | 'resolved-downstream-active' | 'resolved-awaiting-routing' | 'completed-at-terminal-station'
    has_other_open_entries: boolean
    has_downstream_unresolved_instances: boolean
    station_has_outbound_connections: boolean
    batch_event_id: string | null
}

export interface OpsLaborSummary {
    user_id: string
    total_entries: number
    total_hours: number
    earliest_clock_in: string
    latest_clock_out: string | null
}

export interface ProductEquipmentConfig {
    id: string
    product_id: string
    equipment_id: string
    equipment_name: string
    equipment_code: string
    product_name?: string
    product_code?: string
    product_family_name?: string
    standard_hours: number | null
    created_at: string
    updated_at: string
}

export interface OpsSite {
    id: string
    code: string
    name: string
    description?: string
}

// Breadcrumb: Used by people-tab and technician-history-timeline components
// Breadcrumb: Backend handlers in backend/endpoints/ops.ts → handleGetPeople* / handleGetTeamMetrics
export interface OpsPeopleDashboard {
    total_shift_hours: number
    utilization_pct: number
    effectiveness_pct: number
    jobs_completed: number
    earned_hours: number
    /**
     * Positive-only earned standard hours. Edwards-facing metric:
     * max(share-scaled standard hours - operator actual hours, 0).
     * This is the value shown as "Earned Standard Hrs".
     */
    earned_standard_hours: number
    actual_hours: number
    /**
     * Efficiency: `earned_hours / on-job actual hours * 100`.
     * This is the operator's standard-vs-on-job rate.
     * Breadcrumb: backed by get_ops_people_dashboard.efficiency_pct
     */
    efficiency_pct: number
    /**
     * On-job effectiveness: legacy alias for `efficiency_pct`.
     * Excludes activity / non-batch labor entries from the denominator, so
     * it agrees with the per-row aggregation in the Standard Hours Tracker
     * (sum of per-row earned / sum of per-row actual). Use this for the
     * "how productive when on a job" header tile.
     * Breadcrumb: backed by get_ops_people_dashboard.on_job_effectiveness_pct
     */
    on_job_effectiveness_pct: number
    /**
     * Shift utilization: `on-job actual hours / total_shift_hours * 100`.
     * Capped at 100% (numerator is a strict subset of the denominator).
     * Use this for the "what fraction of clocked time was on jobs vs
     * activities/breaks" header tile.
     * Breadcrumb: backed by get_ops_people_dashboard.shift_utilization_pct
     */
    shift_utilization_pct: number
    /**
     * Hours AHEAD of standard, share-scaled. Σ over the operator's
     * completed batches of max(their share-scaled earned − their actual
     * hours on the batch, 0). The absolute-hours twin of On-job
     * effectiveness; `ahead_hours − behind_hours = earned − actual` on
     * those batches.
     * Breadcrumb: backed by get_ops_people_dashboard.ahead_hours
     */
    ahead_hours: number
    /**
     * Hours BEHIND standard, share-scaled. Σ over the operator's
     * completed batches of max(their actual hours on the batch − their
     * share-scaled earned, 0).
     * Breadcrumb: backed by get_ops_people_dashboard.behind_hours
     */
    behind_hours: number
}

/**
 * One labor entry on the operator pages — a single (user, station,
 * batch) clock-in / clock-out window. Source: backend
 * `get_ops_people_history()` over `oee_time_entries`.
 *
 * Aggregation gotchas: see field JSDoc below for `earned_hours`,
 * `effectiveness_pct`, and `machine_event_id`. The short version:
 * earned_hours is per-BATCH denormalized (not per-entry), so summing
 * it naively across entries multiplies the real standard. Use
 * `aggregateLaborMetrics()` from
 * `frontend/src/app/features/oee-dashboard/utils/laborMetrics.ts`
 * which dedupes by `machine_event_id` before summing.
 */
export interface OpsPeopleHistoryEntry {
    id: string
    user_id: string
    user_name: string | null
    job_id: string | null
    /**
     * NULL for activities — labor entries that have no associated
     * job (training, breaks, mock_touch_dwell test fixtures, shift
     * containers). Activities sit in their own labor-history tab and
     * do NOT contribute to byJob rollups.
     */
    job_number: string | null
    batch_id?: string | null
    /**
     * `oee_machine_events.id` for the station-instance this entry
     * belongs to — i.e. the parent batch run. **Preferred** key when
     * deduping per-batch values in aggregations.
     *
     * Gotcha: `oee_time_entries.batch_id` has no FK constraint and
     * can reference either `oee_batch_details.id` OR
     * `oee_machine_events.id` depending on the seed — when it points
     * at `me.id`, the SQL `LEFT JOIN oee_batch_details` in
     * `get_ops_people_history` misses and `machine_event_id` arrives
     * NULL on the wire. `aggregateLaborMetrics()` therefore falls
     * back to `batch_id` (which is still a stable per-batch identity
     * for shifts on the same job/station) and finally to the
     * time-entry `id` as a last resort. See `dedupKey()` in
     * `frontend/src/app/features/oee-dashboard/utils/laborMetrics.ts`
     * and the multi-shift regression in `laborMetrics.spec.ts`.
     */
    machine_event_id?: string | null
    batch_number?: string | null
    station_name: string | null
    family_name: string | null
    /**
     * Specific product (not the family). Rendered as the per-job subtitle in
     * the Job Journey rows on the operator detail page (every other
     * batch/job surface in the app shows product name, not family).
     * Breadcrumb: get_ops_people_history SQL function returns p.product_name
     * (database/sql_scripts/functions/ops/ops_people_analytics_functions.sql).
     * Breadcrumb: rendered by operator-detail-page.component.html → .family span.
     */
    product_name: string | null
    clock_in_ts: string
    clock_out_ts: string | null
    /**
     * Per-entry wall-clock labor — clock_out minus clock_in for THIS
     * entry only. Safe to sum across entries: the operator's total
     * labor in range = SUM(duration_hours).
     */
    duration_hours: number | null
    notes: string | null
    tags: string[] | null
    /**
     * **PER-OPERATOR, PER-BATCH DENORMALIZED.** Equals
     * `pec.standard_hours × bd.good_parts × (this user's actual on
     * batch ÷ all users' actual on batch)` — the operator's share
     * of the batch's full standard hours. Every labor entry on the
     * same batch by the SAME operator returns the SAME value.
     *
     * **DO NOT sum naively across entries** — three entries on the
     * same batch will triple-count the operator's share. Use
     * `aggregateLaborMetrics()` from
     * `frontend/src/app/features/oee-dashboard/utils/laborMetrics.ts`,
     * which dedupes by `machine_event_id` before summing.
     *
     * Backed by the share-scaling formula in
     * `database/sql_scripts/functions/ops/ops_people_analytics_functions.sql`
     * (`get_ops_people_history` header comment); regression coverage at
     * `database/tests/test_operator_earned_hours_share_scaling.sql` and
     * `frontend/src/app/features/oee-dashboard/utils/laborMetrics.spec.ts`.
     * Cross-section consistency on the operator detail page is locked
     * in by `frontend/tests/e2e/operator-metrics-consistency.spec.ts`.
     */
    earned_hours: number | null
    /**
     * **PER-ENTRY**, but computed against the per-batch
     * `earned_hours` for this entry's batch slice — so it's only
     * meaningful for a single entry. **NOT meaningful when summed
     * across entries.** Re-derive from
     * `aggregateLaborMetrics().totals.effectivenessPct`
     * (= deduped earnedHours / summed laborHours).
     */
    effectiveness_pct: number | null
    is_multi_job?: boolean
    allocated_batch_numbers?: string[] | null
}

export interface OpsTeamMetrics {
    total_technicians: number
    avg_utilization_pct: number
    avg_effectiveness_pct: number
    total_labor_hours: number
    total_jobs_completed: number
}

export interface OpsPrimaryStationSummary {
    station_id: string | null
    station_name: string
    hours: number | null
}

export interface OpsLeaderboardEntry {
    user_id: string
    user_name: string | null
    total_hours: number
    /** Actual clocked hours that resolve to a job/batch. */
    job_hours: number
    jobs_completed: number
    earned_hours: number
    /**
     * Positive-only earned standard hours. Uses the same value as
     * `ahead_hours`, but is named for the collapsed operator column.
     */
    earned_standard_hours: number | null
    /** Standard earned hours divided by on-job actual hours. */
    efficiency_pct: number
    /** Standard earned hours divided by total clocked hours. */
    effectiveness_pct: number
    /** On-job actual hours divided by total clocked hours. */
    utilization_pct: number
    // Primary stations within the requested date range, ordered by labor hours.
    primary_station_id: string | null
    primary_station_name: string | null
    primary_station_hours: number | null
    primary_stations?: OpsPrimaryStationSummary[] | null
    // Live state — NOT scoped to the date range. Active = an open
    // (end_ts IS NULL) entry exists right now for this user at this site.
    active_entry_id: string | null
    active_entry_station: string | null
    active_entry_job_number: string | null
    active_entry_product_name: string | null
    active_entry_activity: string | null
    active_entry_started_at: string | null
    // Most recent CLOSED entry for the user; surfaces in the "Off shift" subline.
    last_entry_station: string | null
    last_entry_job_number: string | null
    last_entry_product_name: string | null
    last_entry_ended_at: string | null
    // Hours of in-range labor whose `tags` overlapped the canonical NVA set,
    // plus job labor on rework visits.
    nva_hours: number | null
    // The rework portion of nva_hours, broken out as its own column.
    rework_hours: number | null
    // Share-scaled hours ahead of / behind standard. Same per-batch
    // clamp-then-sum as the detail page so list and detail agree.
    // Breadcrumb: backed by get_ops_people_leaderboard.ahead_hours / behind_hours
    ahead_hours: number | null
    behind_hours: number | null
}

export interface OpsSiteTechnician {
    user_id: string
    user_name: string | null
}

export type DailyAreaEffectivenessArea = 'DCI' | 'Build' | 'TPF'

export interface DailyAreaCompletedJob {
    job_number: string
    work_order: string | null
    completed_at: string
    booked_hours: number
    stations: string[]
    people: Pick<DailyAreaPerson, 'user_id' | 'user_name'>[]
}

export interface DailyAreaPerson {
    user_id: string
    user_name: string
    stations: string[]
    available_hours: number
}

export interface DailyAreaEffectivenessResult {
    work_date: string
    area: DailyAreaEffectivenessArea
    completed_pumps: number
    booked_hours: number
    people_count: number
    actual_hours: number
    effectiveness_pct: number | null
    completed_jobs: DailyAreaCompletedJob[]
    people: DailyAreaPerson[]
}

// ============================================
// Service
// ============================================

@Injectable({
    providedIn: 'root'
})
export class OpsService {
    constructor(private _requestService: RequestService, private http: HttpClient) {}

    getStations(siteId: string): Observable<OpsStation[]> {
        const url = `${environment.api}/oee/equipment?site_id=${siteId}`
        const options = { error: { operation: 'Get Stations' } } as EndpointOptions
        return this._requestService.get<Array<Record<string, unknown>>>(url, options).pipe(
            map(rows => (Array.isArray(rows) ? rows : [])
                .filter(row => row['node_type'] === 'station')
                .map(row => ({
                    id: String(row['id'] ?? ''),
                    site_id: String(row['site_id'] ?? siteId),
                    station_code: String(row['code'] ?? ''),
                    station_name: String(row['name'] ?? ''),
                    is_archived: Boolean(row['is_archived']),
                    machine_only: Boolean(row['machine_only']),
                    allows_batch_operators: row['allows_batch_operators'] === undefined
                        ? undefined
                        : Boolean(row['allows_batch_operators']),
                    allow_concurrent_jobs: Boolean(row['allow_concurrent_jobs'])
                }))
            )
        )
    }

    // ============================================
    // Station Equipment
    // ============================================

    getStationEquipment(stationId: string): Observable<StationEquipment[]> {
        const url = `${environment.api}/ops/station/${stationId}/equipment`
        const options = { error: { operation: 'Get Station Equipment' } } as EndpointOptions
        return this._requestService.get<StationEquipment[]>(url, options)
    }

    setStationEquipment(stationId: string, equipmentIds: string[]): Observable<{ success: boolean }> {
        const url = `${environment.api}/ops/station/${stationId}/equipment`
        const options = { error: { operation: 'Set Station Equipment' } } as EndpointOptions
        return this._requestService.update<{ success: boolean }>(url, { equipment_ids: equipmentIds }, options)
    }

    // ============================================
    // Station Connections
    // ============================================

    createStationConnection(body: {
        site_id: string; from_station_id: string; to_station_id: string;
        label?: string; exit_side?: string; entry_side?: string; sort_order?: number
    }): Observable<OpsStationConnection> {
        const url = `${environment.api}/ops/station-connection`
        const options = { error: { operation: 'Create Station Connection' } } as EndpointOptions
        return this._requestService.create<OpsStationConnection>(url, body, options)
    }

    getStationConnections(siteId: string): Observable<OpsStationConnection[]> {
        const url = `${environment.api}/ops/station-connections/${siteId}`
        const options = { error: { operation: 'Get Station Connections' } } as EndpointOptions
        return this._requestService.get<OpsStationConnection[]>(url, options)
    }

    updateStationConnection(connectionId: string, body: Partial<Pick<OpsStationConnection, 'label' | 'exit_side' | 'entry_side' | 'sort_order'>>): Observable<OpsStationConnection> {
        const url = `${environment.api}/ops/station-connection/${connectionId}`
        const options = { error: { operation: 'Update Station Connection' } } as EndpointOptions
        return this._requestService.update<OpsStationConnection>(url, body, options)
    }

    deleteStationConnection(connectionId: string): Observable<void> {
        const url = `${environment.api}/ops/station-connection/${connectionId}`
        const options = { error: { operation: 'Delete Station Connection' } } as EndpointOptions
        return this._requestService.delete<void>(url, options)
    }

    // ============================================
    // Product Families
    // ============================================

    createProductFamily(body: {
        site_id: string; family_code: string; family_name: string; description?: string
    }): Observable<OpsProductFamily> {
        const url = `${environment.api}/ops/product-family`
        const options = { error: { operation: 'Create Product Family' } } as EndpointOptions
        return this._requestService.create<OpsProductFamily>(url, body, options)
    }

    getProductFamilies(siteId: string): Observable<OpsProductFamily[]> {
        const url = `${environment.api}/ops/product-families?site_id=${siteId}`
        const options = { error: { operation: 'Get Product Families' } } as EndpointOptions
        return this._requestService.get<OpsProductFamily[]>(url, options)
    }

    getProductFamilyById(familyId: string): Observable<OpsProductFamily> {
        const url = `${environment.api}/ops/product-family/${familyId}`
        const options = { error: { operation: 'Get Product Family' } } as EndpointOptions
        return this._requestService.get<OpsProductFamily>(url, options)
    }

    updateProductFamily(familyId: string, body: Partial<Pick<OpsProductFamily,
        'family_code' | 'family_name' | 'description'
    >>): Observable<OpsProductFamily> {
        const url = `${environment.api}/ops/product-family/${familyId}`
        const options = { error: { operation: 'Update Product Family' } } as EndpointOptions
        return this._requestService.update<OpsProductFamily>(url, body, options)
    }

    archiveProductFamily(familyId: string): Observable<void> {
        const url = `${environment.api}/ops/product-family/${familyId}`
        const options = { error: { operation: 'Archive Product Family' } } as EndpointOptions
        return this._requestService.delete<void>(url, options)
    }

    unarchiveProductFamily(familyId: string): Observable<void> {
        const url = `${environment.api}/ops/product-family/${familyId}/restore`
        const options = { error: { operation: 'Restore Product Family' } } as EndpointOptions
        return this._requestService.patch<void>(url, {}, options)
    }

    // ============================================
    // Products
    // ============================================

    /** In-memory products cache keyed by site_id. Populated by getProducts(). */
    private productsCache = new Map<string, OpsProduct[]>()

    getProducts(siteId: string): Observable<OpsProduct[]> {
        const url = `${environment.api}/ops/products?site_id=${siteId}`
        const options = { error: { operation: 'Get Products' } } as EndpointOptions
        return this._requestService.get<OpsProduct[]>(url, options).pipe(
            tap(products => this.productsCache.set(siteId, products))
        )
    }

    /** Synchronously return the cached products list for a site, or [] if not fetched yet. */
    getCachedProducts(siteId: string): OpsProduct[] {
        return this.productsCache.get(siteId) ?? []
    }

    createProduct(body: {
        family_id: string; product_code: string; product_name: string; description?: string;
        default_quantity?: number; target_scrap_rate?: number; unit_value?: number;
        site_id?: string
    }): Observable<OpsProduct> {
        const url = `${environment.api}/ops/product`
        const options = { error: { operation: 'Create Product' } } as EndpointOptions
        return this._requestService.create<OpsProduct>(url, body, options)
    }

    updateProduct(productId: string, body: Partial<Pick<OpsProduct, 'product_code' | 'product_name' | 'description' | 'default_quantity' | 'target_scrap_rate' | 'unit_value' | 'is_active' | 'family_id'>>): Observable<OpsProduct> {
        const url = `${environment.api}/ops/product/${productId}`
        const options = { error: { operation: 'Update Product' } } as EndpointOptions
        return this._requestService.update<OpsProduct>(url, body, options)
    }

    deleteProduct(productId: string): Observable<void> {
        const url = `${environment.api}/ops/product/${productId}`
        const options = { error: { operation: 'Delete Product' } } as EndpointOptions
        return this._requestService.delete<void>(url, options)
    }

    checkProductUsage(productId: string): Observable<{ batch_count: number }> {
        const url = `${environment.api}/ops/product/${productId}/usage`
        const options = { error: { operation: 'Check Product Usage' } } as EndpointOptions
        return this._requestService.get<{ batch_count: number }>(url, options)
    }

    // ============================================
    // Labor Entries
    // ============================================

    clockIn(body: { site_id: string; batch_id?: string; clock_in_ts?: string; tags?: string[]; is_rework?: boolean | null }): Observable<OpsLaborEntry> {
        const url = `${environment.api}/ops/labor/clock-in`
        const options = { error: { operation: 'Clock In' } } as EndpointOptions
        return this._requestService.create<OpsLaborEntry>(url, body, options)
    }

    clockOut(entryId: string, clockOutTs?: string): Observable<OpsLaborEntry> {
        const url = `${environment.api}/ops/labor/${entryId}/clock-out`
        const options = { error: { operation: 'Clock Out' } } as EndpointOptions
        return this._requestService.create<OpsLaborEntry>(url, { clock_out_ts: clockOutTs ?? null }, options)
    }

    completeLaborEntry(entryId: string, body: { clock_in_ts?: string; clock_out_ts: string; notes?: string | null; complete_job?: boolean }): Observable<CompletedLaborEntryResult> {
        const url = `${environment.api}/ops/labor/${entryId}/complete`
        const options = { error: { operation: 'Complete Time Entry' } } as EndpointOptions
        return this._requestService.create<CompletedLaborEntryResult>(url, body, options)
    }

    /**
     * Returns operators with an open time entry on the given batch_event_id(s),
     * EXCLUDING the current user. Drives the "this will clock out other
     * operators" warning before complete_batch.
     *
     * Pass a single id (the common single-job path) or an array (multi-job
     * entries that resolve N jobs at once). Multiple ids are forkJoined and
     * the resulting operator lists are merged + de-duplicated by user_id, so
     * the warning modal shows each co-worker once regardless of how many of
     * the multi-job's batches they're on.
     */
    getActiveCoworkersOnBatchEvent(batchEventId: string | string[]): Observable<{ user_id: string; name: string | null }[]> {
        const ids = (Array.isArray(batchEventId) ? batchEventId : [batchEventId]).filter(Boolean)
        if (ids.length === 0) return of([])

        const options = { error: { operation: 'Get Active Operators on Batch' } } as EndpointOptions
        const requests = ids.map(id =>
            this._requestService.get<{ operators: { user_id: string; name: string | null }[] }>(
                `${environment.api}/ops/batch-events/${id}/active-operators`,
                options
            ).pipe(map(res => res?.operators ?? []))
        )
        return forkJoin(requests).pipe(
            map(lists => {
                const seen = new Map<string, { user_id: string; name: string | null }>()
                for (const list of lists) for (const op of list) {
                    if (!seen.has(op.user_id)) seen.set(op.user_id, op)
                }
                return Array.from(seen.values())
            })
        )
    }

    getLaborEntries(siteId: string, userId?: string, startDate?: string, endDate?: string): Observable<OpsLaborEntry[]> {
        let url = `${environment.api}/ops/labor?site_id=${siteId}`
        if (userId) url += `&user_id=${userId}`
        // Append local timezone offset so the DB interprets day boundaries correctly
        const tzOffset = this.getLocalTimezoneOffset()
        if (startDate) url += `&start_date=${startDate.length === 10 ? startDate + 'T00:00:00' + tzOffset : startDate}`
        if (endDate) url += `&end_date=${endDate.length === 10 ? endDate + 'T23:59:59' + tzOffset : endDate}`
        const options = { error: { operation: 'Get Labor Entries' } } as EndpointOptions
        return this._requestService.get<OpsLaborEntry[]>(url, options)
    }

    getLaborEntryById(entryId: string): Observable<OpsLaborEntry> {
        const url = `${environment.api}/ops/labor/${entryId}`
        const options = { error: { operation: 'Get Labor Entry' } } as EndpointOptions
        return this._requestService.get<OpsLaborEntry>(url, options)
    }

    getLaborEntryStationOptions(entryId: string): Observable<LaborEntryStationOption[]> {
        const url = `${environment.api}/ops/labor/${entryId}/station-options`
        const options = { error: { operation: 'Get Station Options' } } as EndpointOptions
        return this._requestService.get<LaborEntryStationOption[]>(url, options)
    }

    updateLaborEntry(entryId: string, body: {
        batch_id?: string | null
        batch_event_id?: string | null
        clock_in_ts?: string
        clock_out_ts?: string | null
        duration_hours?: number | null
        notes?: string | null
        tags?: string[]
        classification?: LaborEntryClassification
        is_rework?: boolean | null
        confirm_retract?: boolean
    }): Observable<OpsLaborEntry> {
        const url = `${environment.api}/ops/labor/${entryId}`
        const options = { error: { operation: 'Update Labor Entry' } } as EndpointOptions
        return this._requestService.update<OpsLaborEntry>(url, body, options)
    }

    deleteLaborEntry(entryId: string): Observable<void> {
        const url = `${environment.api}/ops/labor/${entryId}`
        const options = { error: { operation: 'Delete Labor Entry' } } as EndpointOptions
        return this._requestService.delete<void>(url, options)
    }

    // ============================================
    // Batch Time Entry operations (for editing batch entries from My Shifts)
    // ============================================

    updateBatchTimeEntry(timeEntryId: string, body: { start_ts?: string; end_ts?: string | null; notes?: string | null; is_rework?: boolean | null; confirm_retract?: boolean }): Observable<unknown> {
        const url = `${environment.api}/oee/batch/time-entry/${timeEntryId}`
        const options = { error: { operation: 'Update Batch Time Entry' } } as EndpointOptions
        return this._requestService.update<unknown>(url, body, options)
    }

    deleteBatchTimeEntry(timeEntryId: string): Observable<void> {
        const url = `${environment.api}/oee/batch/time-entry/${timeEntryId}`
        const options = { error: { operation: 'Delete Batch Time Entry' } } as EndpointOptions
        return this._requestService.delete<void>(url, options)
    }

    stopBatchTimeEntry(timeEntryId: string, endTs?: string): Observable<unknown> {
        const url = `${environment.api}/oee/batch/time-entry/${timeEntryId}/stop`
        const body = endTs ? { end_ts: endTs } : {}
        const options = { error: { operation: 'Stop Batch Time Entry' } } as EndpointOptions
        return this._requestService.update<unknown>(url, body, options)
    }

    getRecentNotes(siteId: string): Observable<string[]> {
        const url = `${environment.api}/ops/labor/notes-history?site_id=${siteId}`
        const options = { error: { operation: 'Get Recent Notes' } } as EndpointOptions
        return this._requestService.get<unknown[]>(url, options).pipe(
            map(notes =>
                Array.isArray(notes)
                    ? notes
                        .filter((note): note is string => typeof note === 'string')
                        .map(note => note.trim())
                        .filter(note => note.length > 0)
                    : []
            )
        )
    }
    getLaborSummaryByBatch(batchId: string): Observable<OpsLaborSummary[]> {
        const url = `${environment.api}/ops/labor/summary/batch/${batchId}`
        const options = { error: { operation: 'Get Labor Summary by Batch' } } as EndpointOptions
        return this._requestService.get<OpsLaborSummary[]>(url, options)
    }

    getAvailableBatches(siteId: string, includeCompleted = false): Observable<AvailableBatch[]> {
        let url = `${environment.api}/ops/labor/available-batches?site_id=${siteId}`
        if (includeCompleted) url += '&include_completed=true'
        const options = { error: { operation: 'Get Available Batches' } } as EndpointOptions
        return this._requestService.get<AvailableBatch[]>(url, options)
    }
    getStationJobs(siteId: string): Observable<StationJob[]> {
        const url = `${environment.api}/ops/station-jobs?site_id=${siteId}`
        const options = { error: { operation: 'Get Station Jobs' } } as EndpointOptions
        return this._requestService.get<StationJob[]>(url, options)
    }

    unResolveMachineEvent(eventId: string): Observable<{ ok: boolean }> {
        const url = `${environment.api}/ops/machine-event/${eventId}/un-resolve`
        const options = {
            error: { operation: 'Un-resolve job' },
            successToast: { title: 'Job un-resolved' }
        } as EndpointOptions
        return this._requestService.create<{ ok: boolean }>(url, {}, options)
    }

    getStationDowntimeEvents(stationId: string, siteId: string, startDate?: string, endDate?: string): Observable<StationDowntimeEvent[]> {
        let url = `${environment.api}/ops/station/${stationId}/downtime-events?site_id=${siteId}`
        if (startDate && endDate) {
            const tzOffset = this.getLocalTimezoneOffset()
            url += `&start_date=${encodeURIComponent(startDate.length === 10 ? startDate + 'T00:00:00' + tzOffset : startDate)}`
            url += `&end_date=${encodeURIComponent(endDate.length === 10 ? endDate + 'T23:59:59' + tzOffset : endDate)}`
        }
        const options = { error: { operation: 'Get Station Downtime Events' } } as EndpointOptions
        return this._requestService.get<StationDowntimeEvent[]>(url, options)
    }

    getActiveDowntimeCounts(siteId: string): Observable<{ station_id: string, active_count: number }[]> {
        const url = `${environment.api}/ops/active-downtime-counts?site_id=${siteId}`
        const options = { error: { operation: 'Get Active Downtime Counts' } } as EndpointOptions
        return this._requestService.get<{ station_id: string, active_count: number }[]>(url, options)
    }

    getStationDowntimeSummaries(siteId: string, startDate: string, endDate: string): Observable<StationDowntimeSummary[]> {
        let url = `${environment.api}/ops/downtime-station-summaries?site_id=${siteId}`
        const tzOffset = this.getLocalTimezoneOffset()
        url += `&start_date=${encodeURIComponent(startDate.length === 10 ? startDate + 'T00:00:00' + tzOffset : startDate)}`
        url += `&end_date=${encodeURIComponent(endDate.length === 10 ? endDate + 'T23:59:59' + tzOffset : endDate)}`
        const options = { error: { operation: 'Get Station Downtime Summaries' } } as EndpointOptions
        return this._requestService.get<StationDowntimeSummary[]>(url, options)
    }

    ensureBatchAtStation(body: {
        equipment_id: string
        source_batch_event_id: string
        start_ts?: string
        notes?: string | null
        /** Skip the auto-started time entry for the caller — used when only ROUTING the batch. */
        skip_time_entries?: boolean
        is_rework?: boolean | null
    }): Observable<EnsuredBatchAtStation> {
        const url = `${environment.api}/ops/labor/ensure-batch`
        const options = { error: { operation: 'Ensure Batch At Station' } } as EndpointOptions
        return this._requestService.create<EnsuredBatchAtStation>(url, body, options)
    }

    logCompletedJobSlice(body: {
        site_id: string
        equipment_id: string
        source_batch_event_id: string
        clock_in_ts: string
        clock_out_ts: string
        notes?: string | null
        tags?: string[] | null
        // Breadcrumb: Default false; Add Shift modal does NOT pass this. When omitted
        // the backend logs the labor entry only and does NOT auto-complete the batch
        // (mirrors handleCompleteLaborEntry's complete_job pattern). Callers must opt in
        // explicitly to auto-complete (e.g., a future 'Mark Complete' UI control).
        complete_job?: boolean
        is_rework?: boolean | null
    }): Observable<LoggedCompletedJobSlice> {
        const url = `${environment.api}/ops/labor/log-completed-job-slice`
        const options = { error: { operation: 'Log Completed Job Slice' } } as EndpointOptions
        return this._requestService.create<LoggedCompletedJobSlice>(url, body, options)
    }

    logMultiJobEntry(body: {
        site_id: string
        equipment_id: string
        batch_event_ids: string[]
        clock_in_ts: string
        clock_out_ts?: string
        notes?: string | null
        tags?: string[] | null
        is_rework?: boolean | null
    }): Observable<{ entries: any[]; batch_event_ids: string[] }> {
        const url = `${environment.api}/ops/labor/log-multi-job`
        const options = { error: { operation: 'Log Multi-Job Entry' } } as EndpointOptions
        return this._requestService.create<{ entries: any[]; batch_event_ids: string[] }>(url, body, options)
    }

    getTimeEntryAllocations(entryId: string): Observable<TimeEntryAllocation[]> {
        const url = `${environment.api}/ops/labor/${entryId}/allocations`
        const options = { error: { operation: 'Get Time Entry Allocations' } } as EndpointOptions
        return this._requestService.get<TimeEntryAllocation[]>(url, options)
    }

    updateTimeEntryAllocations(entryId: string, batchEventIds: string[]): Observable<{ success: boolean }> {
        const url = `${environment.api}/ops/labor/${entryId}/allocations`
        const options = { error: { operation: 'Update Time Entry Allocations' } } as EndpointOptions
        return this._requestService.update<{ success: boolean }>(url, { batch_event_ids: batchEventIds }, options)
    }

    fillGap(siteId: string, clockIn: string, clockOut: string, notes: string, batchEventId?: string, tags?: string[]): Observable<{ success: boolean }> {
        const url = `${environment.api}/ops/labor/fill-gap`
        const body: any = { site_id: siteId, clock_in_ts: clockIn, clock_out_ts: clockOut, notes }
        if (batchEventId) body.batch_event_id = batchEventId
        if (tags) body.tags = tags
        const options = {
            error: { operation: 'Fill Gap' },
            successToast: { title: 'Gap filled' }
        } as EndpointOptions
        return this._requestService.create<{ success: boolean }>(url, body, options)
    }

    getActiveClockIn(): Observable<OpsLaborEntry | null> {
        const url = `${environment.api}/ops/labor/active`
        const options = { error: { operation: 'Get Active Clock In' } } as EndpointOptions
        return this._requestService.get<OpsLaborEntry | null>(url, options)
    }

    exportLaborEntriesToCsv(siteId: string, filename: string, userId?: string, startDate?: string, endDate?: string): Observable<Blob> {
        let url = `${environment.api}/ops/labor/export?site_id=${siteId}`
        if (userId) url += `&user_id=${userId}`
        if (startDate) url += `&start_date=${startDate}`
        if (endDate) url += `&end_date=${endDate}`
        const options: EndpointOptions = {
            error: { operation: 'Export Shifts to CSV' },
            httpOptions: { responseType: 'blob' },
            successToast: { title: 'Shifts Exported to CSV' }
        }
        return this._requestService.get<Blob>(url, options).pipe(
            tap(blob => saveAs(blob, filename))
        )
    }

    // ============================================
    // Sites (lightweight accessor for shared components)
    // ============================================

    getSites(): Observable<OpsSite[]> {
        const url = `${environment.api}/oee/sites`
        const options = { error: { operation: 'Get Sites' } } as EndpointOptions
        return this._requestService.get<OpsSite[]>(url, options)
    }

    // ============================================
    // Product–Equipment Configs
    // ============================================

    getProductEquipmentConfigs(productId: string): Observable<ProductEquipmentConfig[]> {
        const url = `${environment.api}/ops/product/${productId}/equipment-config`
        const options = { error: { operation: 'Get Product Equipment Configs' } } as EndpointOptions
        return this._requestService.get<ProductEquipmentConfig[]>(url, options)
    }

    getEquipmentProductConfigs(equipmentId: string): Observable<ProductEquipmentConfig[]> {
        const url = `${environment.api}/ops/equipment/${equipmentId}/product-config`
        const options = { error: { operation: 'Get Equipment Product Configs' } } as EndpointOptions
        return this._requestService.get<ProductEquipmentConfig[]>(url, options)
    }

    createProductEquipmentConfig(config: Pick<ProductEquipmentConfig, 'product_id' | 'equipment_id' | 'standard_hours'>): Observable<ProductEquipmentConfig> {
        const url = `${environment.api}/ops/product-equipment-config`
        const options = { error: { operation: 'Create Product Equipment Config' } } as EndpointOptions
        return this._requestService.create<ProductEquipmentConfig>(url, config, options)
    }

    updateProductEquipmentConfig(configId: string, standardHours: number | null): Observable<ProductEquipmentConfig> {
        const url = `${environment.api}/ops/product-equipment-config/${configId}`
        const options = { error: { operation: 'Update Product Equipment Config' } } as EndpointOptions
        return this._requestService.update<ProductEquipmentConfig>(url, { standard_hours: standardHours }, options)
    }

    upsertProductEquipmentConfigs(productId: string, configs: Partial<ProductEquipmentConfig>[]): Observable<ProductEquipmentConfig[]> {
        const url = `${environment.api}/ops/product/${productId}/equipment-configs`
        const options = { error: { operation: 'Upsert Product Equipment Configs' } } as EndpointOptions
        return this._requestService.update<ProductEquipmentConfig[]>(url, { configs }, options)
    }

    deleteProductEquipmentConfig(configId: string): Observable<void> {
        const url = `${environment.api}/ops/product-equipment-config/${configId}`
        const options = { error: { operation: 'Delete Product Equipment Config' } } as EndpointOptions
        return this._requestService.delete<void>(url, options)
    }

    // ============================================
    // People Analytics
    // Breadcrumb: Routes in backend/index.ts → /api/v1/ops/people/*
    // Breadcrumb: Handlers in backend/endpoints/ops.ts → handleGetPeople* / handleGetTeamMetrics
    // ============================================

    getPeopleDashboard(siteId: string, userId?: string | null, startDate?: string, endDate?: string): Observable<OpsPeopleDashboard> {
        let url = `${environment.api}/ops/people/dashboard?site_id=${siteId}`
        if (userId) url += `&user_id=${userId}`
        if (startDate) url += `&start_date=${startDate}`
        if (endDate) url += `&end_date=${endDate}`
        const options = { error: { operation: 'Get People Dashboard' } } as EndpointOptions
        return this._requestService.get<OpsPeopleDashboard>(url, options)
    }

    getPeopleHistory(siteId: string, filters?: {
        user_id?: string; job_search?: string; product_family_id?: string;
        station_id?: string; start_date?: string; end_date?: string
    }): Observable<OpsPeopleHistoryEntry[]> {
        let url = `${environment.api}/ops/people/history?site_id=${siteId}`
        if (filters?.user_id) url += `&user_id=${filters.user_id}`
        if (filters?.job_search) url += `&job_search=${encodeURIComponent(filters.job_search)}`
        if (filters?.product_family_id) url += `&product_family_id=${filters.product_family_id}`
        if (filters?.station_id) url += `&station_id=${filters.station_id}`
        if (filters?.start_date) url += `&start_date=${filters.start_date}`
        if (filters?.end_date) url += `&end_date=${filters.end_date}`
        const options = { error: { operation: 'Get People History' } } as EndpointOptions
        return this._requestService.get<OpsPeopleHistoryEntry[]>(url, options)
    }

    getTeamMetrics(siteId: string, startDate?: string, endDate?: string): Observable<OpsTeamMetrics> {
        let url = `${environment.api}/ops/people/team?site_id=${siteId}`
        if (startDate) url += `&start_date=${startDate}`
        if (endDate) url += `&end_date=${endDate}`
        const options = { error: { operation: 'Get Team Metrics' } } as EndpointOptions
        return this._requestService.get<OpsTeamMetrics>(url, options)
    }

    getPeopleLeaderboard(siteId: string, startDate?: string, endDate?: string): Observable<OpsLeaderboardEntry[]> {
        let url = `${environment.api}/ops/people/leaderboard?site_id=${siteId}`
        if (startDate) url += `&start_date=${startDate}`
        if (endDate) url += `&end_date=${endDate}`
        const options = { error: { operation: 'Get People Leaderboard' } } as EndpointOptions
        return this._requestService.get<OpsLeaderboardEntry[]>(url, options)
    }

    /**
     * Breadcrumb: GET /api/v1/ops/people/daily-area-effectiveness is handled
     * by backend/endpoints/ops.ts and rendered by OperatorsListComponent.
     */
    getDailyAreaEffectiveness(
        siteId: string,
        startDate: string,
        endDate: string,
        timezone: string
    ): Observable<DailyAreaEffectivenessResult[]> {
        const url = `${environment.api}/ops/people/daily-area-effectiveness` +
            `?site_id=${encodeURIComponent(siteId)}` +
            `&start_date=${encodeURIComponent(startDate)}` +
            `&end_date=${encodeURIComponent(endDate)}` +
            `&timezone=${encodeURIComponent(timezone)}`
        const options = { error: { operation: 'Get Daily Area Effectiveness' } } as EndpointOptions
        return this._requestService.get<DailyAreaEffectivenessResult[]>(url, options)
    }

    getSiteTechnicians(siteId: string): Observable<OpsSiteTechnician[]> {
        const url = `${environment.api}/ops/people/site-technicians?site_id=${siteId}`
        const options = { error: { operation: 'Get Site Technicians' } } as EndpointOptions
        return this._requestService.get<OpsSiteTechnician[]>(url, options)
    }

    // ============================================
    // Station Batch Routing
    // ============================================

    routeBatchToStation(siteId: string, stationId: string, sourceStationId: string, batchNumber: string, workOrder: string | null, sourceBatchId: string | null): Observable<any> {
        const url = `${environment.api}/ops/station-queue/route`
        const options = { error: { operation: 'Route Batch to Station' } } as EndpointOptions
        return this._requestService.create<any>(url, {
            site_id: siteId,
            station_id: stationId,
            source_station_id: sourceStationId,
            batch_number: batchNumber,
            work_order: workOrder,
            source_batch_id: sourceBatchId
        }, options)
    }

    getDownstreamStagingStations(stationId: string, siteId: string): Observable<{ stations: { id: string, code: string, name: string }[], has_outbound_connections: boolean }> {
        const url = `${environment.api}/ops/station/${stationId}/downstream-staging?site_id=${siteId}`
        const options = { error: { operation: 'Get Downstream Staging Stations' } } as EndpointOptions
        return this._requestService.get<any>(url, options)
    }

    resolveBatchEvent(eventId: string): Observable<any> {
        const url = `${environment.api}/ops/batch-event/${eventId}/resolve`
        const options = { error: { operation: 'Resolve Batch Event' } } as EndpointOptions
        return this._requestService.update<any>(url, {}, options)
    }

    checkBatchDownstream(stationId: string, batchNumber: string, siteId: string): Observable<{ exists: boolean, downstream_station_id: string | null }> {
        const url = `${environment.api}/ops/station/${stationId}/downstream-check?batch_number=${encodeURIComponent(batchNumber)}&site_id=${siteId}`
        const options = { error: { operation: 'Check Batch Downstream' } } as EndpointOptions
        return this._requestService.get<any>(url, options)
    }

    private getLocalTimezoneOffset(): string {
        const offset = new Date().getTimezoneOffset()
        const sign = offset <= 0 ? '+' : '-'
        const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0')
        const mins = String(Math.abs(offset) % 60).padStart(2, '0')
        return `${sign}${hours}:${mins}`
    }
}
